import React, {Dispatch, SetStateAction, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {generatePath, useParams} from 'react-router-dom';

import {toast} from 'react-toastify';

import {ReactComponent as CheckIcon} from 'assets/svg/check.svg';
import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';

import {
    CopyButton,
    Id,
    StatusWrapper,
    TitleText,
    TitleWrapper,
} from 'components//ui-kit/PageComponents';
import {BackTo} from 'components/common/BackTo';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Helper} from 'components/ui-kit/Helper';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Modal} from 'components/ui-kit/Modal';
import {Spinner} from 'components/ui-kit/Spinner';

import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {DECLARATIONS_STATUS_COLORS} from 'core/constants/statuses';
import {GdsStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {dataState$, Gate, setConfirmEvent, setReviewEvent} from 'models/declarations/detail';
import {colors} from 'theme';
import {GridFieldConfig, UserPermission} from 'types';
import {DeclarationCodeFormattedRow, DeclarationDetails} from 'types/declarations';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';

import {Wrapper} from '../styled';

import {
    ButtonsWrapper,
    Column,
    ColumnElement,
    ColumnsWrapper,
    HeaderTypography,
    ModalWrapper,
    RejectButton,
    SpinnerContainer,
    StatusBlock,
    Title,
    Typography,
} from './styled';

export function convertData(
    data: DeclarationDetails | null,
): DeclarationDetails<DeclarationCodeFormattedRow> | null {
    if (!data) return null;
    const DATE_PATTERN = 'dd.MM.yyyy hh:mm';

    return {
        ...data,
        receiptedAt: formatDate(data.receiptedAt ?? '', DATE_PATTERN),
        customsMarkingCodeList: data.customsMarkingCodeList.map((d) => ({
            ...d,
            updatedAt: formatDate(d.updatedAt ?? '', DATE_PATTERN),
            code: d.objectsNumber ? (
                <Link
                    to={{
                        pathname: generatePath(ROUTE.DECLARATION_TRANSPORT_CODES, {
                            id: data.id,
                            transportCode: d.code,
                        }),
                    }}
                >
                    {d.code}
                </Link>
            ) : (
                <>{d.code}</>
            ),
        })),
    };
}

const getGridColumns = (t: TFunction): GridFieldConfig[] => [
    {
        id: 'code',
        header: t('code'),
    },
    {
        id: 'storageLocation',
        header: t('storageLocation'),
    },
    {
        id: 'updatedAt',
        header: t('updatedAt'),
    },
    {
        id: 'objectsNumber',
        header: t('objectsNumber'),
    },
];

type ReviewConfirmModalProps = {
    review: boolean;
    reviewValue: string;
    setReview: Dispatch<SetStateAction<boolean>>;
    setConfirmValue: Dispatch<SetStateAction<string>>;
    id: string;
    t: TFunction;
};

const ReviewConfirmModal = ({
    review,
    reviewValue,
    setReview,
    setConfirmValue,
    id,
    t,
}: ReviewConfirmModalProps) => (
    <Modal show={review} onClose={() => setReview(false)} title={t('review.title')} modalWidth={0}>
        <ModalWrapper>
            <Input
                label={t('review.label')}
                value={reviewValue}
                onChange={(e) => setConfirmValue(e.target.value)}
            />
            <div>
                <Button
                    variant={ButtonVariant.Outline}
                    onClick={() => {
                        setReview(false);
                        setConfirmValue('');
                    }}
                >
                    Cancel
                </Button>
                <Button
                    disabled={!reviewValue}
                    onClick={() => {
                        setReviewEvent({id, gd_number: reviewValue});
                        setReview(false);
                    }}
                >
                    Review
                </Button>
            </div>
        </ModalWrapper>
    </Modal>
);

export const DeclarationDetailsPage = () => {
    const {t: translate} = useTranslation();
    const t = (key: string) => translate('pages.declarations.details.' + key);

    const state = useStore(dataState$);

    const {id} = useParams<{id: string}>();
    useGate(Gate, {id});

    const mappedData = React.useMemo(() => convertData(state.data), [state.data, translate]);
    const columns = useMemo(() => mapTableColumns(getGridColumns(t)), [translate]);

    const copy = useClipboard();
    const copyCode = () => {
        copy(mappedData?.id ?? '');
        toast.success(translate('pages.documents.details.idCopied'));
    };

    const Control = () => (
        <ButtonsWrapper>
            <RejectButton
                variant={ButtonVariant.Outline}
                onClick={() => setConfirmEvent({id, confirm: false})}
            >
                <Icon source={CrossIcon} width={16} height={16} />
                Reject
            </RejectButton>
            <Button onClick={() => setConfirmEvent({id, confirm: true})}>
                <Icon source={CheckIcon} width={24} height={24} />
                Confirm
            </Button>
        </ButtonsWrapper>
    );

    const [review, setReview] = React.useState(false);
    const [reviewValue, setConfirmValue] = React.useState('');

    const Review = () => (
        <Button style={{width: 'fit-content'}} onClick={() => setReview(true)}>
            Review
        </Button>
    );

    return state.isLoading ? (
        <SpinnerContainer>
            <Spinner />
        </SpinnerContainer>
    ) : (
        <>
            <BackTo to={ROUTE.DECLARATIONS}>Back to GD</BackTo>
            <Wrapper>
                <TitleWrapper>
                    <TitleText>
                        <Title>
                            <span style={{fontSize: 20, marginRight: 10}}>{t('title')}</span>{' '}
                            <Id>{mappedData?.id}</Id>
                        </Title>
                        <CopyButton
                            icon={
                                <Icon
                                    source={CopyIcon}
                                    width={15}
                                    height={15}
                                    fillColor={colors.brandPrimary}
                                />
                            }
                            onClick={copyCode}
                            hoverIndication={true}
                            label={translate('pages.documents.details.copyId')}
                            labelPlace={'bottom'}
                        />
                    </TitleText>
                    <StatusBlock>
                        <StatusWrapper
                            color={DECLARATIONS_STATUS_COLORS[mappedData?.status ?? 'DEFAULT']}
                        >
                            {translate('dictionaries.declarationStatuses.' + mappedData?.status)}
                        </StatusWrapper>
                        {mappedData?.status === GdsStatus.REJECTED_AUTOMATIC && (
                            <Helper
                                content={translate(
                                    'pages.documents.gds.statusRejectedAutomaticHelper',
                                    {
                                        defaultValue:
                                            'Rejected automatically due to code disaggregation',
                                    },
                                )}
                            />
                        )}
                    </StatusBlock>
                    <PermissionAccess
                        permissions={[UserPermission.CUSTOMS_DECLARATION_CONFIRM_OR_REJECT]}
                    >
                        {mappedData?.status === 'REVIEW' && <Control />}
                    </PermissionAccess>
                    <PermissionAccess
                        permissions={[UserPermission.CUSTOMS_DECLARATION_CHANGE_TO_REVIEW_STATUS]}
                    >
                        {mappedData?.status === 'SUBMITTED' && <Review />}
                    </PermissionAccess>
                    {/* {mappedData?.status === 'SUBMITTED' && <Review />}
                    {mappedData?.status === 'REVIEW' && <Control />} */}
                </TitleWrapper>
                <ColumnsWrapper>
                    <Column>
                        <ColumnElement>
                            <Title>{t('generalTitle')}</Title>
                        </ColumnElement>
                        <ColumnElement>
                            <HeaderTypography>{t('participantName')}</HeaderTypography>
                            <Typography highlighted>{mappedData?.participantName}</Typography>
                        </ColumnElement>
                        <ColumnElement>
                            <HeaderTypography>{t('receiptedAt')}</HeaderTypography>
                            <Typography highlighted>{mappedData?.receiptedAt}</Typography>
                        </ColumnElement>
                    </Column>
                    <Column>
                        <ColumnElement>
                            <Title>&nbsp;</Title>
                        </ColumnElement>
                        <ColumnElement>
                            <HeaderTypography>{t('participantTin')}</HeaderTypography>
                            <Typography highlighted>{mappedData?.participantTin}</Typography>
                        </ColumnElement>
                        <ColumnElement>
                            <HeaderTypography>{t('storageLocation')}</HeaderTypography>
                            <Typography highlighted>{mappedData?.storageLocation}</Typography>
                        </ColumnElement>
                    </Column>
                </ColumnsWrapper>
                <ColumnElement>
                    <Title>{t('codesTitle')}</Title>
                </ColumnElement>
                <Table
                    columns={columns}
                    data={mappedData?.customsMarkingCodeList}
                    isLoading={state.isLoading}
                    hideSetting={true}
                />
                <PermissionAccess
                    permissions={[UserPermission.CUSTOMS_DECLARATION_CHANGE_TO_REVIEW_STATUS]}
                >
                    <ReviewConfirmModal
                        review={review}
                        reviewValue={reviewValue}
                        setReview={setReview}
                        setConfirmValue={setConfirmValue}
                        id={id}
                        t={t}
                    />
                </PermissionAccess>
            </Wrapper>
        </>
    );
};
